import * as React from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { FormError } from '@/components/ui/form-error'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { AuthShell } from '@/components/AuthShell'

/**
 * The page an emailed sign-in link opens. See `docs/magic-links.md`.
 *
 * **It does not sign anybody in on arrival**, and that is the whole reason this
 * page exists rather than a route that spends the token directly. Corporate mail
 * security — Defender, Proofpoint, Mimecast — fetches every link in a message to
 * scan it before the recipient ever sees it. A link that signed you in when it
 * was loaded would be spent by the scanner, and the person would arrive at "this
 * link is invalid or has expired" having done nothing wrong.
 *
 * A button rather than a submit on mount, for the same reason one step further
 * out: a scanner that runs the page's JavaScript would trip an automatic submit
 * too. One tap buys a link that works every time.
 */
export function MagicLinkPage() {
  const { signInWithMagicLink } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [token] = React.useState<string>(() => params.get('token') ?? '')
  const [isSigningIn, setIsSigningIn] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string | null>(null)

  /**
   * Take the token out of the address bar once it has been read.
   *
   * It is single use and short-lived, but until it is spent it is a live
   * credential, and leaving it in the URL puts it in browser history and on the
   * `Referer` of anything this page loads next. The value is already held in
   * state above, so removing it costs nothing.
   */
  React.useEffect(() => {
    if (window.location.search.length > 0) {
      window.history.replaceState(null, '', window.location.pathname)
    }
  }, [])

  async function handleSignIn(): Promise<void> {
    setError(null)
    setIsSigningIn(true)
    try {
      const redirectTo = await signInWithMagicLink(token)
      navigate(redirectTo ?? '/', { replace: true })
    } catch (cause) {
      setError(requestErrorMessage(cause))
      setIsSigningIn(false)
    }
  }

  return (
    <AuthShell>
      <Card size="lg" className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-display">Sign in</CardTitle>
          <CardDescription>
            {token === ''
              ? 'This link is missing its code. Ask for a new one from the sign-in page.'
              : 'You asked for a link. Press the button and you are in.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {token === '' ? null : (
            <Button pending={isSigningIn} className="w-full" onClick={() => void handleSignIn()}>
              {isSigningIn ? 'Signing in…' : 'Sign in'}
            </Button>
          )}

          {/* The API says the same thing for expired, already used, superseded
              and never-real. Passing it through keeps that one sentence intact
              rather than inventing a more specific one the server refused to. */}
          {error === null ? null : <FormError>{error}</FormError>}

          <div className="text-center">
            <Link to="/login" className="text-sm text-ink-muted underline">
              Back to sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </AuthShell>
  )
}
